import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "../lib/api";
import AdminLayout from "./AdminLayout";
import { useAppT } from "../components/home/i18n";
import { CheckCircle2 } from "lucide-react";

export default function AdminEmployers() {
  const { a } = useAppT();
  const [emps, setEmps] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [reasonFor, setReasonFor] = useState(null); // {id, action}
  const [reason, setReason] = useState("");

  const load = async () => { const { data } = await API.get("/admin/employers"); setEmps(data.employers); };
  useEffect(() => { load(); }, []);

  const act = async (id, action, body = {}) => {
    try { await API.post(`/admin/employers/${id}/${action}`, body); toast.success(a.adminEmp.done); setReasonFor(null); setReason(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const shown = emps.filter((e) => filter === "all" ? true : e.status === filter);
  const tone = { pending: "text-[#b0722a]", verified: "text-[#2E6B4B]", rejected: "text-[#8B3A3A]", revoked: "text-[#8B3A3A]" };
  const fLabel = a.adminEmp.filters;
  const sWord = (s) => a.adminEmp.statuses[s] || s;

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-serif text-3xl text-[#173A2C]">{a.adminEmp.title}</h1>
        <div className="flex gap-1 rounded-full border border-[#E8E4DB] bg-white p-1">
          {["pending", "verified", "all"].map((f) => {
            const n = f === "all" ? emps.length : emps.filter((e) => e.status === f).length;
            return (
              <button key={f} data-testid={`empfilter-${f}`} onClick={() => setFilter(f)} className={`rounded-full px-3 py-1 text-sm ${filter === f ? "bg-[#1F4A38] text-white" : "text-[#5C5855]"}`}>{fLabel[f]} {n}</button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 space-y-3" data-testid="admin-employers-list">
        {shown.length === 0 && <div className="text-sm text-[#5C5855]">{a.common.nothingHere}</div>}
        {shown.map((e) => (
          <div key={e.id} className="rounded-md border border-[#E8E4DB] bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="font-serif text-lg text-[#173A2C]">{e.org_name} <span className={`ml-2 text-xs font-semibold ${tone[e.status]}`}>{sWord(e.status)}</span></div>
                <div className="text-sm text-[#5C5855]">{e.country} · {a.adminEmp.regPrefix} {e.reg_number || "—"} · {e.website || a.adminEmp.noSite}</div>
                <div className="text-sm text-[#5C5855]">{a.adminEmp.contact}: {e.contact_name} {e.contact_role ? `(${e.contact_role})` : ""} · {e.contact_phone || ""}</div>
                <div className="text-xs text-[#8a8580]">{a.adminEmp.account}: {e.account?.email}</div>
                {e.review_reason && <div className="mt-1 text-xs text-[#8B3A3A]">{a.adminJobs.reasonPrefix} {e.review_reason}</div>}
              </div>
              <div className="flex flex-col items-end gap-2">
                <button data-testid={`verify-${e.id}`} onClick={() => act(e.id, "verify")} className="inline-flex items-center gap-1 rounded-full bg-[#2E6B4B] px-4 py-1.5 text-xs font-semibold text-white"><CheckCircle2 className="h-3.5 w-3.5" /> {a.adminEmp.verify}</button>
                <button data-testid={`reject-${e.id}`} onClick={() => setReasonFor({ id: e.id, action: "reject" })} className="rounded-full border border-[#D28E3D] px-4 py-1.5 text-xs font-semibold text-[#b0722a]">{a.adminEmp.reject}</button>
                {e.status === "verified" && <button data-testid={`revoke-${e.id}`} onClick={() => setReasonFor({ id: e.id, action: "revoke" })} className="rounded-full border border-[#8B3A3A] px-4 py-1.5 text-xs font-semibold text-[#8B3A3A]">{a.adminEmp.revoke}</button>}
              </div>
            </div>
            {reasonFor?.id === e.id && (
              <div className="mt-3 rounded-sm border border-[#E8E4DB] p-3">
                <textarea data-testid="emp-reason-input" rows="2" value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder={`${a.adminEmp.reasonToPrefix}${reasonFor.action === "reject" ? a.adminEmp.reject : a.adminEmp.revoke}…`} className="w-full rounded-sm border border-[#E8E4DB] px-3 py-2 text-sm outline-none focus:border-[#1F4A38]" />
                <div className="mt-2 flex gap-2">
                  <button data-testid="emp-reason-submit" onClick={() => reasonFor.action === "reject" && !reason.trim() ? toast.error(a.adminEmp.reasonRequired) : act(e.id, reasonFor.action, { reason })} className="rounded-sm bg-[#8B3A3A] px-3 py-1.5 text-xs font-semibold text-white">{reasonFor.action === "reject" ? a.adminEmp.reject : a.adminEmp.revoke}</button>
                  <button onClick={() => { setReasonFor(null); setReason(""); }} className="rounded-sm border border-[#E8E4DB] px-3 py-1.5 text-xs">{a.common.cancel}</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
}
